import { useCallback, useState } from 'react'
import { AxiosError } from 'axios'
import { useSharePlan } from '@/hooks/usePlanShares'
import { extractMentionIds } from '@/utils/mentions'
import type { MentionCandidate } from '@/components/common/MentionCommentInput'

function isAlreadySharedError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === 409 &&
    /already has access/i.test(error.response?.data?.detail ?? '')
  )
}

/**
 * Tracks non-collaborator users picked from the @mention dropdown while
 * composing a comment, so the composer can gate posting behind a "share the
 * plan with them first" confirmation (see MentionCommentInput's
 * `isCollaborator` / "Won't notify" signal — mentioning someone without
 * plan access means they never see the comment or get notified).
 */
export function useMentionShareGate(planId: string) {
  const [pending, setPending] = useState<Map<string, MentionCandidate>>(
    new Map()
  )
  const shareMutation = useSharePlan(planId)

  const registerMention = useCallback((user: MentionCandidate) => {
    if (user.isCollaborator) return
    setPending(prev => {
      if (prev.has(user.id)) return prev
      const next = new Map(prev)
      next.set(user.id, user)
      return next
    })
  }, [])

  const resolveUsersToShare = useCallback(
    (content: string): MentionCandidate[] => {
      return extractMentionIds(content)
        .map(id => pending.get(id))
        .filter((u): u is MentionCandidate => !!u)
    },
    [pending]
  )

  const shareWithUsers = useCallback(
    async (users: MentionCandidate[]) => {
      for (const user of users) {
        try {
          await shareMutation.mutateAsync({ user_id: user.id })
        } catch (error) {
          if (!isAlreadySharedError(error)) throw error
        }
      }
    },
    [shareMutation]
  )

  return {
    registerMention,
    resolveUsersToShare,
    shareWithUsers,
    isSharing: shareMutation.isPending,
  }
}
